import constants from '@/config/constants'

const localStorageSync = (store) => {
  // Throttle storage updates per storage key
  const pendingUpdates = {}

  const getLocalStorageKeyUserProject = () => {
    if (!store.state.project.project) {
      return
    }

    const projectId = store.state.project.project._id
    const userId = store.state.currentUser.user ? store.state.currentUser.user._id : null

    if (!projectId || !userId) {
      return
    }

    return [
      constants.LOCAL_STORAGE_PREFIX,
      userId,
      projectId,
    ].join('-')
  }

  const getLocalStorageKeyUser = () => {
    if (!store.state.currentUser.user) {
      return
    }

    const userId = store.state.currentUser.user._id

    if (!userId) {
      return
    }

    return [
      constants.LOCAL_STORAGE_PREFIX,
      userId,
    ].join('-')
  }

  const getLocalStorageKeyDownloadFormat = () => {
    const userId = store.state.currentUser.user && store.state.currentUser.user._id

    // when not logged in, use global setting
    if (!userId) {
      return [
        constants.LOCAL_STORAGE_PREFIX,
        'downloadpage',
      ].join('-')
    }

    // when logged in, use user specific setting across projects
    return [
      constants.LOCAL_STORAGE_PREFIX,
      userId,
    ].join('-')
  }

  const throttledUpdateStorage = ({ storageKey }) => {
    if (!storageKey) throw new Error('storageKey is missing')

    // If there's already a pending update for this storage key, do nothing
    // The existing timeout will capture the latest state when it executes
    if (pendingUpdates[storageKey]) {
      return
    }

    // Set new timeout - this won't be cancelled by subsequent updates
    const timeoutId = setTimeout(async () => {
      try {
        const matchingVuexStoreConfigs = vuexStoreConfigs.filter(config => config.getStorageKey() === storageKey)

        if (matchingVuexStoreConfigs.length === 0) {
          return
        }

        const vuexStoreData = {}

        for (const vuexStoreConfig of matchingVuexStoreConfigs) {
          const iteratingVuexStoreName = vuexStoreConfig.vuexStoreName

          for (const iteratingVuexStoreProp of vuexStoreConfig.vuexStorePropertiesToSync) {
            if (!vuexStoreData[iteratingVuexStoreName]) {
              vuexStoreData[iteratingVuexStoreName] = {}
            }

            vuexStoreData[iteratingVuexStoreName][iteratingVuexStoreProp] = store.state[iteratingVuexStoreName][iteratingVuexStoreProp]
          }
        }

        localStorage.setItem(
          storageKey,
          JSON.stringify(vuexStoreData),
        )
      } catch (error) {
        throw new Error('Failed to sync to localStorage', { cause: error })
      } finally {
        delete pendingUpdates[storageKey]
      }
    }, constants.VUEX_TO_LOCALSTORAGE_SYNC_DELAY_IN_MS)

    pendingUpdates[storageKey] = timeoutId
  }

  // storeNames which should be stored in the localstorage
  // Can be either a string, or an object, defining which exact vuexStorePropertiesToSync should get saved
  const vuexStoreConfigs = [
    {
      vuexStoreName: 'plans',
      vuexStorePropertiesToSync: [
        'sortSettings',
        'printSettings',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'plansNewPrefill',
      vuexStorePropertiesToSync: [
        'isEnabled',
        'features',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'plansListModus',
      vuexStorePropertiesToSync: [
        'modus',
        'filterStoreName',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'documents',
      vuexStorePropertiesToSync: [
        'sortSettings',
        'printSettings',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'documentsNewPrefill',
      vuexStorePropertiesToSync: [
        'isEnabled',
        'features',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'documentsListModus',
      vuexStorePropertiesToSync: [
        'modus',
        'filterStoreName',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'tasks',
      vuexStorePropertiesToSync: [
        'subTab',
        'sortSettings',
        'printSettings',
        'listSettings',
        'listStyle',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'tasksNewPrefill',
      vuexStorePropertiesToSync: [
        'isEnabled',
        'features',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'tasksListModus',
      vuexStorePropertiesToSync: [
        'subTab',
        'modus',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'photos',
      vuexStorePropertiesToSync: [
        'listStyle',
        'showMetadata',
        'groupBy',
        'sortProperty',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'photosNewPrefill',
      vuexStorePropertiesToSync: [
        'isEnabled',
        'features',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'photosListModus',
      vuexStorePropertiesToSync: [
        'modus',
        'filterStoreName',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'participants',
      vuexStorePropertiesToSync: [
        'sortSettings',
        'printSettings',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'participantsListModus',
      vuexStorePropertiesToSync: [
        'modus',
        'filterStoreName',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'projectsFilter',
      vuexStorePropertiesToSync: [
        'searchString',
        'ownership',
      ],
      getStorageKey: getLocalStorageKeyUser,
    },
    {
      vuexStoreName: 'clipboard',
      vuexStorePropertiesToSync: [
        'files',
        'sortSettings',
        'filter',
      ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'download',
      vuexStorePropertiesToSync: [ 'downloadFormat' ],
      getStorageKey: getLocalStorageKeyDownloadFormat,
    },
    {
      vuexStoreName: 'recipientGroups',
      vuexStorePropertiesToSync: [ 'isOpen' ],
      getStorageKey: getLocalStorageKeyUserProject,
    },
    {
      vuexStoreName: 'projects',
      vuexStorePropertiesToSync: [ 'sortProperty' ],
      getStorageKey: getLocalStorageKeyUser,
    },
    {
      vuexStoreName: 'mailer',
      vuexStorePropertiesToSync: [
        'pdfOnly',
        'selectedItems',
        'receiverEmails',
        'addedParticipants',
        'subject',
        'bodyText',
        'showRecipientsList',
      ],
      getStorageKey: getLocalStorageKeyUser,
    },
    // We switched to not save the pfFile list in the localStorage, because bigger projects exeeds the localStorage size limit.
    // {
    //   vuexStoreName: 'pfFiles',
    //   vuexStorePropertiesToSync: ['list', 'lastFetchedAt'],
    //   getStorageKey: getLocalStorageKeyUserProject,
    // },
  ]

  // Runs for EVERY mutation
  store.subscribe((mutation, state) => {
    const mutationStoreName = mutation.type.split('/')[0]

    const vuexStoreConfig = vuexStoreConfigs.find(conf => mutationStoreName === conf.vuexStoreName)

    if (!vuexStoreConfig) {
      return
    }

    const storageKey = vuexStoreConfig.getStorageKey()

    if (!storageKey) {
      return
    }

    throttledUpdateStorage({
      storageKey,
    })
  })
}

export default localStorageSync
